// cart.js — in-memory shopping cart with localStorage persistence and a
// pub/sub API. Pure logic + DOM rendering helpers; no Three.js.

const STORAGE_KEY = 'retro-bakery-cart-v1';

// id → { product, qty }
const _items = new Map();
const _listeners = new Set();

// ---------- Persistence ----------
export function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return;
    for (const entry of arr) {
      if (entry && entry.product && entry.product.id && entry.qty > 0) {
        _items.set(entry.product.id, { product: entry.product, qty: entry.qty });
      }
    }
  } catch (e) {
    console.warn('[cart] failed to load from storage:', e);
  }
}

function _save() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([..._items.values()]));
  } catch (e) {
    console.warn('[cart] failed to save:', e);
  }
}

// ---------- Mutations ----------
// Returns true on success, false if rejected (missing/invalid product or
// sold out). Caller decides whether to surface a "sold out" toast — this
// function deliberately stays UI-free.
export function addItem(product) {
  if (!product || !product.id) return false;
  if (product.available === false) return false;
  const existing = _items.get(product.id);
  if (existing) existing.qty++;
  else _items.set(product.id, { product, qty: 1 });
  _save(); _notify();
  return true;
}

export function removeItem(id) {
  if (_items.delete(id)) { _save(); _notify(); }
}

export function setQty(id, qty) {
  const it = _items.get(id);
  if (!it) return;
  if (qty <= 0) _items.delete(id);
  else it.qty = qty;
  _save(); _notify();
}

export function clear() {
  _items.clear();
  _save(); _notify();
}

// Sync each cart line against the freshly-loaded product list. Backfills
// `variantId` for carts persisted before live inventory wiring existed,
// updates prices that drifted on Shopify, and drops items that disappeared
// from the catalog entirely. Sold-out items stay in the cart visually but
// will be filtered out at checkout time (so the user can see why their
// total looks off).
export function reconcileWithProducts(products) {
  const byId = new Map(products.map(p => [p.id, p]));
  let changed = false;
  for (const id of [..._items.keys()]) {
    const entry = _items.get(id);
    const fresh = byId.get(id);
    if (!fresh) {
      _items.delete(id);
      changed = true;
      continue;
    }
    // Replace the cached product reference so variantId/available/price all
    // stay aligned with the latest load.
    entry.product = fresh;
    changed = true;
  }
  if (changed) { _save(); _notify(); }
}

// ---------- Queries ----------
export function getItems() { return [..._items.values()]; }
export function getCount() {
  let n = 0;
  for (const it of _items.values()) n += it.qty;
  return n;
}
export function getTotal() {
  let t = 0;
  for (const it of _items.values()) t += it.qty * Number(it.product.price);
  return t;
}

// ---------- Pub/sub ----------
export function subscribe(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}
function _notify() {
  for (const fn of _listeners) {
    try { fn(); } catch (e) { console.error('[cart] listener error:', e); }
  }
}

// ---------- DOM rendering ----------
// Renders the cart drawer's contents and updates the floating counter badge.
// Idempotent — safe to call on every change.
export function renderCart() {
  const list = document.getElementById('cart-items');
  const total = document.getElementById('cart-total');
  const count = document.getElementById('cart-count');
  const empty = document.getElementById('cart-empty');

  if (count) count.textContent = String(getCount());
  if (total) total.textContent = '$' + getTotal().toFixed(2);

  if (!list) return;

  // Hide the dedicated empty message when there are items
  const items = getItems();
  if (empty) empty.hidden = items.length > 0;

  list.innerHTML = '';
  for (const { product, qty } of items) {
    const li = document.createElement('li');
    li.className = 'cart-item';

    const thumb = document.createElement('img');
    thumb.className = 'cart-thumb';
    thumb.src = product.imageUrl;
    thumb.alt = '';
    thumb.loading = 'lazy';
    thumb.referrerPolicy = 'no-referrer';

    const meta = document.createElement('div');
    meta.className = 'cart-meta';

    const name = document.createElement('div');
    name.className = 'cart-name';
    name.textContent = product.name;

    const price = document.createElement('div');
    price.className = 'cart-price';
    price.textContent = '$' + Number(product.price).toFixed(2) + ' each';

    const qtyRow = document.createElement('div');
    qtyRow.className = 'cart-qty';

    const dec = document.createElement('button');
    dec.type = 'button';
    dec.className = 'qty-btn';
    dec.textContent = '−';
    dec.addEventListener('click', () => setQty(product.id, qty - 1));

    const qSpan = document.createElement('span');
    qSpan.className = 'qty-num';
    qSpan.textContent = String(qty);

    const inc = document.createElement('button');
    inc.type = 'button';
    inc.className = 'qty-btn';
    inc.textContent = '+';
    inc.addEventListener('click', () => setQty(product.id, qty + 1));

    qtyRow.append(dec, qSpan, inc);

    meta.append(name, price, qtyRow);

    const lineTotal = document.createElement('div');
    lineTotal.className = 'cart-line-total';
    lineTotal.textContent = '$' + (qty * Number(product.price)).toFixed(2);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'cart-remove';
    remove.setAttribute('aria-label', 'Remove from cart');
    remove.textContent = '×';
    remove.addEventListener('click', () => removeItem(product.id));

    li.append(thumb, meta, lineTotal, remove);
    list.appendChild(li);
  }
}
